import { useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, FileText, Lock, LoaderCircle, Users } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { matchTemplates, renderTemplate, unresolvedTokens } from "@/lib/messages/message-templates";

const PICKER_WIDTH = 380;

/**
 * One row in either surface. Shows what the template IS (name, scope, shortcut) —
 * the rendered text lives in the preview panel, because a one-line squeeze of a
 * multi-line WhatsApp message reads as noise.
 */
function TemplateOptionRow({ template, active, onPick, onHover }) {
  return (
    <button
      type="button"
      // mousedown, not click: the textarea must not lose focus mid-pick.
      onMouseDown={(event) => {
        event.preventDefault();
        onPick(template);
      }}
      onMouseEnter={onHover}
      className={cn(
        "flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-sm",
        active ? "bg-accent" : "hover:bg-muted",
      )}
    >
      {template.shared ? (
        <Users className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
      ) : (
        <Lock className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
      )}
      <span className="min-w-0 flex-1 truncate">{template.title || "Untitled template"}</span>
      {template.category ? (
        <span className="shrink-0 text-[11px] text-muted-foreground">{template.category}</span>
      ) : null}
      {template.shortcut ? (
        <code className="shrink-0 rounded bg-background px-1 py-0.5 text-[10.5px] text-muted-foreground shadow-[inset_0_0_0_1px_var(--border)]">
          /{template.shortcut}
        </code>
      ) : null}
    </button>
  );
}

/**
 * The rendered result of the highlighted template, plus the count of variables that
 * resolved to nothing.
 *
 * This warning is the whole reason the preview exists: renderTemplate DROPS an empty
 * "*Date:* " line and strips an unknown token, so without it a missing detail leaves
 * no trace for staff to notice before they send.
 */
function TemplatePreview({ template, templateVars }) {
  const rendered = useMemo(
    () => (template ? renderTemplate(template.body, templateVars) : ""),
    [template, templateVars],
  );
  const unresolved = useMemo(
    () => (template ? unresolvedTokens(template.body, templateVars) : []),
    [template, templateVars],
  );

  if (!template) return null;

  return (
    <div className="border-t border-border">
      {unresolved.length ? (
        <div className="flex items-start gap-1.5 border-b border-border bg-amber-500/10 px-2.5 py-1.5 text-[11px] text-amber-700 dark:text-amber-300">
          <AlertTriangle className="mt-0.5 size-3 shrink-0" aria-hidden />
          <span>
            {unresolved.length === 1
              ? "1 variable is unresolved"
              : `${unresolved.length} variables are unresolved`}{" "}
            (<span className="font-mono">{unresolved.join(", ")}</span>) — those parts will be left out.
          </span>
        </div>
      ) : null}
      <ScrollArea viewportClassName="max-h-32 px-2.5 py-2">
        <pre className="whitespace-pre-wrap text-[12px] leading-relaxed text-muted-foreground">
          {rendered || "This template renders to nothing."}
        </pre>
      </ScrollArea>
    </div>
  );
}

function EmptyState({ loading, hasTemplates }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 px-3 py-6 text-[12px] text-muted-foreground">
        <LoaderCircle className="size-3.5 animate-spin" aria-hidden />
        Loading templates…
      </div>
    );
  }
  return (
    <div className="px-3 py-6 text-center text-[12px] text-muted-foreground">
      {hasTemplates
        ? "No template matches."
        : "No templates yet. Create them in Settings → Message templates."}
    </div>
  );
}

/**
 * Compact inline list shown above the composer while a "/" slash command is being
 * typed. Deliberately mirrors the @mention dropdown's shape and keyboard contract —
 * the textarea keeps focus and owns Arrow/Enter/Escape, so typing never jumps.
 *
 * The two can never be open at once: mentions exist only in internal chats and
 * templates only in patient threads. The composer still gives mentions precedence.
 */
export function TemplateSuggestionList({ templates, templateVars, activeIndex, onPick, onHover, loading }) {
  const active = templates[activeIndex] ?? null;
  return (
    <div className="mb-2 overflow-hidden rounded-lg border border-border bg-popover shadow-md">
      {templates.length === 0 ? (
        <EmptyState loading={loading} hasTemplates={false} />
      ) : (
        <>
          <ScrollArea viewportClassName="max-h-40">
            {templates.map((template, index) => (
              <TemplateOptionRow
                key={template.id}
                template={template}
                active={index === activeIndex}
                onPick={onPick}
                onHover={() => onHover(index)}
              />
            ))}
          </ScrollArea>
          <TemplatePreview template={active} templateVars={templateVars} />
        </>
      )}
    </div>
  );
}

/**
 * Composer toolbar template picker: a FileText trigger next to the emoji picker,
 * opening a searchable list of the clinic's shared templates and the user's own.
 *
 * Emits the RENDERED text upward and closes itself; caret handling stays in the
 * composer, exactly like ComposerEmojiPicker.
 *
 * @param {object} props
 * @param {Array<object>} props.templates
 * @param {Record<string,string>} props.templateVars — resolved variable values for this thread
 * @param {(text: string) => void} props.onPickTemplate — receives the rendered message
 * @param {(event: Event) => void} [props.onCloseAutoFocus]
 * @param {() => void} [props.onOpen] — fired when the popover opens, to warm the cache
 */
export function ComposerTemplatePicker({
  templates = [],
  templateVars = {},
  loading = false,
  onPickTemplate,
  onCloseAutoFocus,
  onOpen,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const searchRef = useRef(null);

  const matches = useMemo(() => matchTemplates(templates, query), [templates, query]);
  const active = matches[Math.min(activeIndex, Math.max(0, matches.length - 1))] ?? null;

  useEffect(() => {
    setActiveIndex(0);
  }, [query, open]);

  const handleOpenChange = (next) => {
    setOpen(next);
    if (next) {
      setQuery("");
      onOpen?.();
    }
  };

  const pick = (template) => {
    if (!template) return;
    setOpen(false);
    onPickTemplate?.(renderTemplate(template.body, templateVars));
  };

  const handleSearchKeyDown = (event) => {
    if (matches.length === 0) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((index) => (index + 1) % matches.length);
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((index) => (index - 1 + matches.length) % matches.length);
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      pick(active);
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-muted-foreground hover:text-foreground"
          aria-label="Insert message template"
        >
          <FileText className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="start"
        sideOffset={6}
        className="overflow-hidden p-0"
        style={{ width: PICKER_WIDTH }}
        onCloseAutoFocus={onCloseAutoFocus}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          searchRef.current?.focus();
        }}
      >
        <input
          ref={searchRef}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={handleSearchKeyDown}
          placeholder="Search templates…"
          className="w-full border-b border-border bg-transparent px-2.5 py-2 text-sm text-foreground outline-none placeholder:text-muted-foreground"
        />
        {matches.length === 0 ? (
          <EmptyState loading={loading} hasTemplates={templates.length > 0} />
        ) : (
          <>
            <ScrollArea viewportClassName="max-h-60">
              {matches.map((template, index) => (
                <TemplateOptionRow
                  key={template.id}
                  template={template}
                  active={template.id === active?.id}
                  onPick={pick}
                  onHover={() => setActiveIndex(index)}
                />
              ))}
            </ScrollArea>
            <TemplatePreview template={active} templateVars={templateVars} />
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
